import { useCallback } from 'react';
import { MarketActionCard, MarketList, MarketRow } from '@market/react';
import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import WarningIcon from '@square-icons/react/16/UI/circle-warning';
import { BASE_THEME } from 'theme/shop_theme';
import { trackCdp, trackEvent } from 'utilities/tracking';
import { TRACKING_EVENT_TYPES } from 'components/pages/product/helpers';
import { toCurrencyWithMinorUnitAmount } from 'utilities/currency';
import { useProductContext } from './contexts/product_context';
import {
  getKioskSoftwareAddOn,
  hasKioskSoftwarePricing,
  KioskSoftwareChoice,
} from './kiosk_software';

const KioskSoftwareSelector = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    addOns,
    selection,
    setSelection,
    cart,
    showKioskSoftwareError,
    kioskSoftwareRef,
  } = useProductContext();
  const addOn = getKioskSoftwareAddOn(addOns);
  const options = addOn?.kiosk_software_options || [];
  const pricingAvailable = hasKioskSoftwarePricing(addOn);
  const onSelectionChange = useCallback(
    (event: CustomEvent<{ newSelectionValue: string }>) => {
      const choice = event.detail.newSelectionValue as KioskSoftwareChoice;
      if (
        !addOn ||
        !pricingAvailable ||
        !['plus', 'premium', 'existing'].includes(choice)
      ) {
        return;
      }

      setSelection((previous) => ({
        ...previous,
        kioskSoftware: choice,
        addOns: {
          ...previous.addOns,
          [addOn.category]: addOn.variant_id.toString(),
        },
      }));
      const params = {
        choice: t(`productPage.kioskSoftware.${choice}`),
        subscriptionTier: choice,
        cartNumber: cart?.number,
        ...addOn.tracking_data,
      };
      trackEvent({ name: 'Add On', value: 'Change', params });
      trackCdp({
        name: TRACKING_EVENT_TYPES.ChangeAddOn,
        params: { feature: 'shop', ...params },
      });
    },
    [addOn, pricingAvailable, setSelection, cart?.number, t],
  );

  return (
    <section
      ref={kioskSoftwareRef}
      tabIndex={-1}
      aria-labelledby="kiosk-software-heading"
      aria-describedby={
        showKioskSoftwareError && pricingAvailable
          ? 'kiosk-software-error'
          : undefined
      }
      style={{ marginTop: theme.spacing(3) }}
    >
      <p id="kiosk-software-heading" className="kiosk-section-title">
        {t('productPage.kioskSoftware.title')}
      </p>
      <p
        className="paragraph-20"
        style={{
          fontSize: BASE_THEME.type.fontSize.small,
          lineHeight: BASE_THEME.type.lineHeight.small,
          color: BASE_THEME.color.gray['55'],
          margin: theme.spacing(1, 0, 2),
        }}
      >
        {t('productPage.kioskSoftware.trial')}
      </p>
      {pricingAvailable ? (
        <MarketList
          interactive
          value={selection.kioskSoftware || ''}
          aria-labelledby="kiosk-software-heading"
          onMarketListSelectionsDidChange={onSelectionChange}
        >
          {(['plus', 'premium', 'existing'] as const).map((choice) => {
            const option = options.find(({ id }) => id === choice);
            const planName = t(`productPage.kioskSoftware.${choice}`);
            return (
              <MarketActionCard key={choice} value={choice}>
                <MarketRow>
                  <p slot="label">{planName}</p>
                  <div slot="subtext">
                    {choice !== 'existing' && (
                      <p className="paragraph-20">
                        {t('productPage.kioskSoftware.locationPrice', {
                          price: toCurrencyWithMinorUnitAmount(
                            option.location_price.amount,
                            option.location_price.currency_code,
                          ),
                          planName,
                        })}
                      </p>
                    )}
                    <p className="paragraph-20">
                      {t('productPage.kioskSoftware.devicePrice', {
                        price: toCurrencyWithMinorUnitAmount(
                          option.device_price.amount,
                          option.device_price.currency_code,
                        ),
                      })}
                    </p>
                  </div>
                </MarketRow>
              </MarketActionCard>
            );
          })}
        </MarketList>
      ) : (
        <p role="alert">{t('productPage.kioskSoftware.unavailable')}</p>
      )}
      {showKioskSoftwareError && pricingAvailable && (
        <p
          id="kiosk-software-error"
          role="alert"
          className="paragraph-20"
          style={{
            color: theme.palette.error.main,
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            marginBottom: theme.spacing(1),
          }}
        >
          <WarningIcon
            aria-hidden="true"
            style={{ flexShrink: 0, fill: 'currentColor' }}
          />
          {t('productPage.kioskSoftware.required')}
        </p>
      )}
    </section>
  );
};

export default KioskSoftwareSelector;
